import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, Users, MapPin, Play, Square, Copy, Check, 
  Crown, Navigation2, Share2, Loader2, UserPlus, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Rider marker colors
const RIDER_COLORS = [
  "#F59E0B", // amber (leader)
  "#3B82F6", // blue
  "#10B981", // green
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#EF4444", // red
  "#F97316", // orange
];

const GroupRide = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { user } = useAuth();
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const watchIdRef = useRef(null);
  const updateIntervalRef = useRef(null);
  
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [riderLocations, setRiderLocations] = useState([]);
  const [copied, setCopied] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [joining, setJoining] = useState(false);

  // Load Google Maps
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  // Fetch group data
  const fetchGroup = useCallback(async () => {
    if (!groupId) return;
    try {
      const res = await axios.get(`${API}/group-rides/${groupId}`);
      setGroup(res.data);
    } catch (error) {
      console.error("Failed to fetch group:", error);
      if (error.response?.status === 404) {
        toast.error("Group ride not found");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !window.google || mapInstanceRef.current) return;
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 }, // Center of US
      zoom: 10,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c2c54" }] },
        { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
        { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e4166" }] },
      ],
      disableDefaultUI: true,
      zoomControl: true,
    });
  }, [group]);

  // Watch user location
  useEffect(() => {
    if (!group || group.status !== "active") return;
    
    if (navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            heading: position.coords.heading,
            speed: position.coords.speed
          };
          setCurrentLocation(loc);
          
          // Update location on server
          try {
            await axios.post(`${API}/group-rides/${groupId}/location`, loc);
          } catch (error) {
            console.error("Failed to update location:", error);
          }
        },
        (error) => console.error("Location error:", error),
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    }
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [group?.status, groupId]);

  // Poll for rider locations
  useEffect(() => {
    if (!group || group.status !== "active") return;
    
    const fetchLocations = async () => {
      try {
        const res = await axios.get(`${API}/group-rides/${groupId}/locations`);
        setRiderLocations(res.data.locations || []);
        updateMarkers(res.data.locations || []);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    
    fetchLocations();
    updateIntervalRef.current = setInterval(fetchLocations, 3000); // Every 3 seconds
    
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [group?.status, groupId]);

  // Update map markers
  const updateMarkers = (locations) => {
    if (!mapInstanceRef.current) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    let hasLocations = false;
    
    locations.forEach((rider, index) => {
      if (!rider.location) return;
      
      hasLocations = true;
      const position = { lat: rider.location.lat, lng: rider.location.lng };
      bounds.extend(position);
      
      const color = rider.is_leader ? RIDER_COLORS[0] : RIDER_COLORS[(index % (RIDER_COLORS.length - 1)) + 1];
      
      if (markersRef.current[rider.user_id]) {
        // Update existing marker
        markersRef.current[rider.user_id].setPosition(position);
      } else {
        // Create new marker
        const marker = new window.google.maps.Marker({
          position,
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: rider.is_leader ? 12 : 10,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
          title: rider.name,
          zIndex: rider.is_leader ? 100 : 50
        });
        
        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="color:#000;font-weight:bold;">${rider.name}${rider.is_leader ? ' 👑' : ''}</div>`
        });
        marker.addListener("click", () => infoWindow.open(mapInstanceRef.current, marker));
        
        markersRef.current[rider.user_id] = marker;
      }
    });
    
    // Fit bounds if we have locations
    if (hasLocations && locations.length > 1) {
      mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
    } else if (hasLocations) {
      mapInstanceRef.current.setCenter(bounds.getCenter());
      mapInstanceRef.current.setZoom(14);
    }
  };

  // Copy invite code
  const copyInviteCode = async () => {
    if (!group?.invite_code) return;
    try {
      await navigator.clipboard.writeText(group.invite_code);
      setCopied(true);
      toast.success("Invite code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Share invite
  const shareInvite = async () => {
    if (!group?.invite_code) return;
    
    const shareData = {
      title: `Join my group ride: ${group.name}`,
      text: `Join my group ride "${group.name}" on Rides & Dives!\n\nInvite code: ${group.invite_code}\n\nOpen the app, go to Group Rides, and enter the code to join!`,
      url: window.location.href
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Shared!");
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareData.text);
        toast.success("Invite copied to clipboard!");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // User didn't cancel, actual error
        await navigator.clipboard.writeText(`Join code: ${group.invite_code}`);
        toast.success("Invite code copied!");
      }
    }
  };

  // Start ride
  const handleStart = async () => {
    try {
      await axios.post(`${API}/group-rides/${groupId}/start`);
      toast.success("Ride started!");
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start ride");
    }
  };

  // End ride
  const handleEnd = async () => {
    try {
      await axios.post(`${API}/group-rides/${groupId}/end`);
      toast.success("Ride ended!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to end ride");
    }
  };

  // Leave ride
  const handleLeave = async () => {
    try {
      await axios.post(`${API}/group-rides/${groupId}/leave`);
      toast.success("Left group ride");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave");
    }
  };

  // Join ride with code
  const handleJoin = async () => {
    if (!inviteCode.trim()) return;
    setJoining(true);
    try {
      const res = await axios.post(`${API}/group-rides/${groupId || 'join'}/join`, {
        invite_code: inviteCode.toUpperCase()
      });
      toast.success("Joined group ride!");
      setJoinModalOpen(false);
      if (res.data.group_id) {
        navigate(`/group-ride/${res.data.group_id}`);
      }
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const isLeader = group?.leader_id === user?.user_id;
  const isMember = group?.is_member;

  return (
    <div className="min-h-screen bg-dark flex flex-col">
      {/* Header */}
      <div className="glass sticky top-0 z-40 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-oswald text-lg uppercase tracking-wider flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                {group?.name || "Group Ride"}
              </h1>
              <p className="text-xs text-gray-500">
                {group?.riders?.length || 0} rider{group?.riders?.length !== 1 ? 's' : ''} • {group?.status}
              </p>
            </div>
          </div>
          
          {group?.status === "waiting" && (
            <Button
              size="sm"
              variant="outline"
              className="border-cyan-500/50 text-cyan-400"
              onClick={copyInviteCode}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="ml-1 font-mono">{group?.invite_code}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Map */}
      <div ref={mapRef} className="flex-1 min-h-[300px]" />

      {/* Rider List */}
      <div className="glass rounded-t-2xl -mt-4 relative z-10 p-4">
        <h2 className="font-oswald uppercase text-sm text-gray-400 mb-3">Riders</h2>
        
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {group?.riders?.map((rider, index) => {
            const riderLoc = riderLocations.find(r => r.user_id === rider.user_id);
            const color = rider.is_leader ? RIDER_COLORS[0] : RIDER_COLORS[(index % (RIDER_COLORS.length - 1)) + 1];
            
            return (
              <div
                key={rider.user_id}
                className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
              >
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                  {rider.picture ? (
                    <img src={rider.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm">{rider.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate flex items-center gap-1">
                    {rider.name}
                    {rider.is_leader && <Crown className="w-3 h-3 text-amber-400" />}
                  </p>
                  <p className="text-xs text-gray-500">
                    {riderLoc?.location ? (
                      <span className="text-green-400">● Online</span>
                    ) : (
                      <span className="text-gray-600">● Offline</span>
                    )}
                  </p>
                </div>
                {rider.user_id === user?.user_id && (
                  <span className="text-xs text-bronze">You</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-4 space-y-2">
          {group?.status === "waiting" && isLeader && (
            <Button
              className="w-full h-12 bg-green-600 hover:bg-green-700 font-oswald uppercase"
              onClick={handleStart}
            >
              <Play className="w-5 h-5 mr-2" />
              Start Ride
            </Button>
          )}
          
          {group?.status === "active" && isLeader && (
            <Button
              className="w-full h-12 bg-red-600 hover:bg-red-700 font-oswald uppercase"
              onClick={handleEnd}
            >
              <Square className="w-5 h-5 mr-2" />
              End Ride
            </Button>
          )}
          
          {group?.status === "active" && !isLeader && (
            <div className="flex items-center justify-center gap-2 text-green-400 py-2">
              <Navigation2 className="w-5 h-5 animate-pulse" />
              <span className="font-oswald uppercase">Ride in Progress</span>
            </div>
          )}
          
          {isMember && !isLeader && (
            <Button
              variant="outline"
              className="w-full h-10 border-red-500/50 text-red-400"
              onClick={handleLeave}
            >
              <X className="w-4 h-4 mr-2" />
              Leave Ride
            </Button>
          )}
        </div>
      </div>

      {/* Join Modal */}
      <Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase">Join Group Ride</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="Enter invite code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              className="h-12 bg-[#121212] border-white/10 text-center font-mono text-xl tracking-wider"
              maxLength={6}
            />
            <Button
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
              onClick={handleJoin}
              disabled={joining || inviteCode.length !== 6}
            >
              {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join Ride"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupRide;
